import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner, Table, Form, Badge } from 'react-bootstrap';
import { getAttendance, markAttendance, exportAttendance } from '../../../api/classes';
import { getEventAssignments } from '../../../api/events';

export const EventAttendanceModal = ({ show, onHide, eventId, isOwner }) => {
  const [attendance, setAttendance] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Date for marking new attendance
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState({}); // { studentId: 'present' | 'absent' }
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exportTimePeriod, setExportTimePeriod] = useState('today');

  useEffect(() => {
    if (show && eventId) {
      fetchData();
    }
  }, [show, eventId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [attRes, stdRes] = await Promise.all([
        getAttendance({ event_id: eventId }).catch(() => ({ data: [] })),
        getEventAssignments({ eventId }).catch(() => ({ data: [] }))
      ]);
      
      setAttendance(attRes.data || []);
      
      // Flatten students from assignments
      const assignments = stdRes.data || [];
      const enrolledStudents = [];
      assignments.forEach(assignment => {
        if (assignment.students && Array.isArray(assignment.students)) {
          assignment.students.forEach(student => {
            enrolledStudents.push({ user: student });
          });
        }
      });

      setStudents(enrolledStudents);
      
      // Initialize attendance data for today
      const initialData = {};
      enrolledStudents.forEach(st => {
        if (st.user?._id) {
          initialData[st.user._id] = 'present'; // Default to present
        }
      });
      setAttendanceData(initialData);
      
      
    } catch (err) {
      console.warn('Failed to fetch attendance data', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      
      // The backend API handles marking one user at a time
      const requests = Object.keys(attendanceData).map(studentId => {
        return markAttendance({
          event_id: eventId,
          user_id: studentId,
          attendance: attendanceData[studentId] === 'present'
        });
      });
      
      await Promise.all(requests);
      
      alert('Attendance marked successfully!');
      fetchData(); // Refresh list
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to mark attendance.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="border-0 pb-0 d-flex align-items-center">
        <Modal.Title className="fw-bold m-auto">Class Attendance</Modal.Title>
        <button 
          className="btn btn-link text-dark p-0 position-absolute" 
          style={{ right: '40px' }}
          onClick={() => setShowExportModal(true)}
          title="Export Attendance"
        >
          <i className="bi bi-download fs-5"></i>
        </button>
      </Modal.Header>
      <Modal.Body className="pt-2">
        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
        ) : (
          <div className="d-flex flex-column flex-lg-row gap-4">
            
            {/* View Attendance Records (Left side) */}
            <div className="flex-grow-1">
              <h6 className="fw-bold mb-3">Attendance History</h6>
              {attendance.length === 0 ? (
                <div className="text-center py-4 bg-light rounded-4 border">
                  <p className="text-muted mb-0">No attendance records found.</p>
                </div>
              ) : (
                <div className="table-responsive rounded-4 border">
                  <Table hover className="mb-0 align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th className="border-0 px-4 py-3">Date</th>
                        <th className="border-0 py-3">Student</th>
                        <th className="border-0 py-3 text-end px-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendance.map((record, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-3">
                            <i className="bi bi-calendar3 text-muted me-2"></i>
                            {record.marked_at ? new Date(record.marked_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="py-3 fw-semibold">
                            {record.user_id?.full_name || record.user_id?.email || 'Unknown Student'}
                          </td>
                          <td className="py-3 text-end px-4">
                            <Badge bg={record.attendance ? 'success' : 'danger'} className="rounded-pill px-3 py-2">
                              {record.attendance ? 'PRESENT' : 'ABSENT'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>

            {/* Mark Attendance (Right side) - Only for Owner */}
            {isOwner && (
              <div style={{ minWidth: '350px' }}>
                <div className="bg-light p-4 rounded-4 border sticky-top" style={{ top: '20px' }}>
                  <h6 className="fw-bold mb-3">Mark Attendance</h6>
                  
                  {students.length === 0 ? (
                    <p className="text-muted small">You need to assign students to this class before marking attendance.</p>
                  ) : (
                    <Form onSubmit={handleMarkSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-semibold text-muted">Date</Form.Label>
                        <Form.Control 
                          type="date" 
                          value={attendanceDate}
                          onChange={e => setAttendanceDate(e.target.value)}
                          required
                          max={new Date().toISOString().split('T')[0]}
                        />
                      </Form.Group>
                      
                      <div className="mb-4" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <Form.Label className="small fw-semibold text-muted mb-2 d-block">Students</Form.Label>
                        {students.map(st => {
                          if (!st.user) return null; // Skip if no user data
                          return (
                            <div key={st.user._id} className="d-flex align-items-center justify-content-between mb-2 p-2 bg-white rounded border-sm">
                              <span className="text-truncate flex-grow-1 pe-2" style={{ fontSize: '0.9rem' }}>
                                {st.user.full_name || st.user.email}
                              </span>
                              <Form.Select 
                                size="sm" 
                                style={{ width: '110px' }}
                                value={attendanceData[st.user._id] || 'present'}
                                onChange={(e) => setAttendanceData({...attendanceData, [st.user._id]: e.target.value})}
                              >
                                <option value="present">Present</option>
                                <option value="absent">Absent</option>
                              </Form.Select>
                            </div>
                          );
                        })}
                      </div>

                      <Button variant="primary" type="submit" disabled={submitting} className="w-100 rounded-pill cd-btn-orange border-0">
                        {submitting ? 'Saving...' : 'Save Attendance'}
                      </Button>
                    </Form>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      {/* Export Attendance Bottom Sheet / Modal */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered className="export-attendance-modal">
        <div className="p-4 rounded-4 bg-white">
          <div className="d-flex justify-content-center mb-3">
            <div style={{ width: '40px', height: '4px', backgroundColor: '#e0e0e0', borderRadius: '2px' }}></div>
          </div>
          
          <div className="d-flex align-items-center mb-4">
            <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '50px', height: '50px' }}>
              <i className="bi bi-download fs-4"></i>
            </div>
            <div>
              <h5 className="fw-bold mb-1 text-dark">Export Attendance</h5>
              <small className="text-muted">Download attendance in PDF or Excel format</small>
            </div>
          </div>

          <h6 className="fw-bold mb-3 text-dark">Select Time Period</h6>
          <div className="d-flex flex-wrap gap-2 mb-4">
            {['Today', 'Week', 'Month', 'Custom'].map(period => (
              <button 
                key={period}
                className={`btn rounded-3 px-3 py-2 fw-semibold d-flex align-items-center gap-2 ${exportTimePeriod === period.toLowerCase() ? 'btn-primary' : 'btn-light text-dark'}`}
                onClick={() => setExportTimePeriod(period.toLowerCase())}
              >
                <i className={`bi bi-calendar${period === 'Week' ? '3' : period === 'Today' ? '-event' : ''}`}></i>
                {period}
              </button>
            ))}
          </div>

          <h6 className="fw-bold mb-3 text-dark">Choose Export Format</h6>
          <div className="d-flex gap-3 mb-4">
            <div 
              className={`p-3 rounded-4 flex-fill border ${exportFormat === 'pdf' ? 'bg-primary text-white border-primary' : 'bg-light text-dark border-light'}`}
              style={{ cursor: 'pointer', transition: '0.2s' }}
              onClick={() => setExportFormat('pdf')}
            >
              <i className={`bi bi-file-earmark-pdf fs-2 ${exportFormat === 'pdf' ? 'text-white' : 'text-dark'}`}></i>
              <h6 className="fw-bold mt-2 mb-1">PDF</h6>
              <small className={exportFormat === 'pdf' ? 'text-white-50' : 'text-muted'}>Best for printing</small>
            </div>
            <div 
              className={`p-3 rounded-4 flex-fill border ${exportFormat === 'excel' ? 'bg-primary text-white border-primary' : 'bg-light text-dark border-light'}`}
              style={{ cursor: 'pointer', transition: '0.2s' }}
              onClick={() => setExportFormat('excel')}
            >
              <i className={`bi bi-file-earmark-spreadsheet fs-2 ${exportFormat === 'excel' ? 'text-white' : 'text-dark'}`}></i>
              <h6 className="fw-bold mt-2 mb-1">Excel</h6>
              <small className={exportFormat === 'excel' ? 'text-white-50' : 'text-muted'}>Best for sheets</small>
            </div>
          </div>

          <div className="bg-warning bg-opacity-10 p-3 rounded-3 d-flex align-items-center gap-3 mb-4 border border-warning border-opacity-25">
            <i className="bi bi-info-circle text-warning fs-5"></i>
            <span className="text-dark small">Exported files will be saved to your device storage.</span>
          </div>

          <Button 
            variant="primary" 
            className="w-100 rounded-3 py-3 fw-bold"
            onClick={async () => {
              try {
                let filterType = 'date'; // Default to today
                if (exportTimePeriod === 'week') filterType = 'days';
                else if (exportTimePeriod === 'month') filterType = 'month';
                
                const response = await exportAttendance({
                  event_id: eventId,
                  format: exportFormat,
                  filterType,
                  ...(filterType === 'date' && { date: new Date().toISOString().split('T')[0] }),
                  ...(filterType === 'days' && { days: 7 }),
                  ...(filterType === 'month' && { month: new Date().getMonth() + 1, year: new Date().getFullYear() })
                });

                if (response && response.url) {
                  const link = document.createElement('a');
                  link.href = response.url;
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                } else {
                  alert('No URL received for export.');
                }
                
                setShowExportModal(false);
              } catch (err) {
                alert('Failed to export attendance');
              }
            }}
          >
            Export Attendance
          </Button>
        </div>
      </Modal>
    </Modal>
  );
};
